import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useV2Api } from '../../../hooks/useV2Api'
import { friendlyErrorMessage } from '../../../utils/friendlyError'

/**
 * Email delivery settings — the subsection inside V2SettingsPage → Email.
 *
 * Lets the tenant choose how outbound quote-request emails are sent:
 *   1. Connect Gmail / Microsoft 365 via Nylas (recommended, one-click)
 *   2. Configure their own SMTP (host + credentials)
 *   3. Do nothing — falls back to Marteu's platform default
 *
 * Reads + writes the real /api/v2/me/email endpoints — no mock data.
 */
export default function V2EmailSettings() {
  const api = useV2Api()
  const [params, setParams] = useSearchParams()
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Per-call fallback string so the banner can show context-specific copy
  // when the friendly mapper has nothing better. Keeps the old wording.
  const [errorFallback, setErrorFallback] = useState(null)
  const [banner, setBanner] = useState(null)

  // SMTP form state
  const [smtpHost, setSmtpHost] = useState('')
  const [smtpPort, setSmtpPort] = useState('587')
  const [smtpUser, setSmtpUser] = useState('')
  const [smtpPass, setSmtpPass] = useState('')
  const [smtpFrom, setSmtpFrom] = useState('')
  const [smtpUseTls, setSmtpUseTls] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)

  // On landing from a successful Nylas callback, Nylas redirects to
  // /v2/settings?email=connected. Show a one-time banner, then clean up.
  useEffect(() => {
    const status = params.get('email')
    if (status === 'connected') {
      setBanner({ kind: 'ok', text: 'Email connected. Outbound quote requests will go through your account.' })
    } else if (status === 'error') {
      setBanner({ kind: 'error', text: params.get('message') || 'Email connect failed.' })
    }
    if (status) {
      params.delete('email'); params.delete('message')
      setParams(params, { replace: true })
    }
  }, [params, setParams])

  const load = useCallback(async () => {
    setLoading(true); setError(null); setErrorFallback(null)
    try {
      const res = await api.getEmailSettings()
      setConfig(res?.data || null)
      const s = res?.data?.smtp
      if (s) {
        setSmtpHost(s.host || ''); setSmtpPort(String(s.port || '587'))
        setSmtpUser(s.username || ''); setSmtpFrom(s.from_address || '')
        setSmtpUseTls(s.use_tls !== false)
      } else {
        setSmtpHost(''); setSmtpPort('587'); setSmtpUser(''); setSmtpFrom(''); setSmtpUseTls(true)
      }
      setSmtpPass('')
    } catch (err) {
      setError(err); setErrorFallback('Failed to load email settings')
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { load() }, [load])

  const handleConnectNylas = async (provider) => {
    setError(null); setErrorFallback(null)
    try {
      const res = await api.request('/api/v2/me/email/nylas/connect', {
        method: 'POST',
        body: JSON.stringify({ provider })
      })
      const url = res?.data?.authUrl
      if (!url) throw new Error('Nylas did not return an auth URL')
      window.location.href = url
    } catch (err) {
      setError(err); setErrorFallback('Could not start the email connect flow.')
    }
  }

  const handleRevokeNylas = async () => {
    if (!confirm('Disconnect the connected email account? Outbound emails will fall back to your SMTP (or Marteu default).')) return
    setError(null); setErrorFallback(null)
    try {
      await api.revokeNylas()
      setBanner({ kind: 'ok', text: 'Email disconnected.' })
      await load()
    } catch (err) {
      setError(err); setErrorFallback('Failed to disconnect email account.')
    }
  }

  const handleTestSmtp = async () => {
    setError(null); setErrorFallback(null); setBanner(null); setTesting(true)
    try {
      await api.testSmtp({
        host: smtpHost, port: smtpPort, username: smtpUser,
        password: smtpPass, useTls: smtpUseTls
      })
      setBanner({ kind: 'ok', text: 'SMTP connection successful.' })
    } catch (err) {
      setError(err); setErrorFallback('SMTP connection failed.')
    } finally {
      setTesting(false)
    }
  }

  const handleSaveSmtp = async () => {
    setError(null); setErrorFallback(null); setBanner(null); setSaving(true)
    try {
      await api.putSmtp({
        host: smtpHost, port: parseInt(smtpPort, 10),
        username: smtpUser, password: smtpPass,
        fromAddress: smtpFrom, useTls: smtpUseTls
      })
      setBanner({ kind: 'ok', text: 'SMTP settings saved.' })
      await load()
    } catch (err) {
      setError(err); setErrorFallback('Failed to save SMTP settings.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div style={{ padding: '1rem' }}>Loading email settings…</div>

  const effective = config?.effectiveMode || 'none'
  const oauth = config?.oauth

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h3 style={{ marginTop: 0 }}>Outbound email</h3>
        <p style={{ color: '#8F8F85', fontSize: 14 }}>
          By default, Marteu sends quote-request emails from our platform address.
          Your forwarders know <em>you</em> — connect your own mailbox so the emails
          come from your domain and land in your Sent folder.
        </p>
        <div style={{
          padding: '10px 14px', background: '#F3F2ED', borderRadius: 6,
          fontSize: 13, color: '#23201C'
        }}>
          <strong>Currently using:</strong>{' '}
          {effective === 'nylas' && `OAuth (${oauth?.from_address || 'connected account'})`}
          {effective === 'smtp' && `Your SMTP (${config?.smtp?.from_address})`}
          {effective === 'platform' && 'Marteu platform email'}
          {effective === 'none' && 'Marteu platform email (default)'}
        </div>
      </div>

      {banner && (
        <div style={{
          padding: '10px 14px', borderRadius: 6, fontSize: 13,
          background: banner.kind === 'ok' ? 'rgba(92,122,84,0.12)' : 'rgba(139,58,58,0.12)',
          color: banner.kind === 'ok' ? '#5C7A54' : '#8B3A3A',
          border: `1px solid ${banner.kind === 'ok' ? '#5C7A54' : '#8B3A3A'}`
        }}>
          {banner.text}
        </div>
      )}

      {error && (
        <div style={{
          padding: '10px 14px', borderRadius: 6, fontSize: 13,
          background: 'rgba(139,58,58,0.08)', color: '#8B3A3A',
          border: '1px solid #8B3A3A'
        }}>
          {friendlyErrorMessage(error, errorFallback)}
        </div>
      )}

      {/* Option 1: OAuth via Nylas — hidden until Nylas production access is enabled */}

      {/* TODO(P2-12): re-add Nylas OAuth block when prod access lands */}
      <div style={{ border: '1px solid #E4E2DA', borderRadius: 8, padding: 18 }}>
        <h4 style={{ margin: '0 0 4px' }}>SMTP server</h4>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: '#8F8F85' }}>
          Use this if you have an existing SMTP (including Gmail App Passwords or Microsoft SMTP AUTH).
          We store your password encrypted with AES-256-GCM — it's never visible again once saved.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: 12, color: '#8F8F85' }}>Host</label>
            <input className="v2-form-input" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)}
                   placeholder="smtp.gmail.com" style={{ width: '100%' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#8F8F85' }}>Port</label>
            <input className="v2-form-input" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)}
                   style={{ width: '100%' }} />
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#8F8F85' }}>Username</label>
          <input className="v2-form-input" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)}
                 placeholder="you@acme.com" style={{ width: '100%' }} />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#8F8F85' }}>
            Password {config?.smtp ? <em style={{ opacity: 0.6 }}>(leave blank to keep existing)</em> : ''}
          </label>
          <input className="v2-form-input" type="password" value={smtpPass}
                 onChange={(e) => setSmtpPass(e.target.value)} style={{ width: '100%' }}
                 autoComplete="new-password" />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#8F8F85' }}>From address</label>
          <input className="v2-form-input" value={smtpFrom} onChange={(e) => setSmtpFrom(e.target.value)}
                 placeholder="quotes@acme.com" style={{ width: '100%' }} />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14 }}>
          <input type="checkbox" checked={smtpUseTls} onChange={(e) => setSmtpUseTls(e.target.checked)} />
          Use STARTTLS
        </label>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            className="v2-btn v2-btn--outline"
            onClick={handleTestSmtp}
            disabled={testing || !smtpHost || !smtpUser || !smtpPass}
          >
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          <button
            className="v2-btn v2-btn--primary"
            onClick={handleSaveSmtp}
            disabled={saving || !smtpHost || !smtpUser || !smtpFrom || !smtpPass}
          >
            {saving ? 'Saving…' : 'Save SMTP'}
          </button>
        </div>
      </div>

      {/* Quick guide */}
      <div style={{
        border: '1px dashed #E4E2DA', borderRadius: 8, padding: 16, fontSize: 13,
        color: '#5C5A52', background: '#FBFAF6'
      }}>
        <strong style={{ color: '#23201C' }}>Quick guide: how to get SMTP credentials</strong>
        <ul style={{ marginTop: 8, paddingLeft: 18 }}>
          <li>
            <strong>Gmail:</strong> Enable 2FA on your Google account → Google Account →
            Security → 2-Step Verification → App passwords → generate one for "Mail".
            Use <code>smtp.gmail.com</code> / port <code>587</code> / your Gmail address /
            the App Password as the password.
          </li>
          <li style={{ marginTop: 6 }}>
            <strong>Microsoft 365:</strong> Admin must enable SMTP AUTH for your mailbox.
            Use <code>smtp.office365.com</code> / port <code>587</code> / your M365
            address / your account password.
          </li>
          <li style={{ marginTop: 6 }}>
            <strong>Company SMTP:</strong> Ask IT for SMTP relay details. Typical values are
            port 587 with STARTTLS or 465 with TLS.
          </li>
        </ul>
        <div style={{ marginTop: 8 }}>
          Having trouble? Contact support for assistance with email configuration.
        </div>
      </div>
    </div>
  )
}
